import React from "react";

import { cn } from "@/lib/style";

interface PlaceCardImageProps {
  imageUrl: string
  imageAlt: string
  aspectRatio: '1:1' | '6:5' | '16:9' | '4:3'
  showSaveIndicator?: boolean
  className?: string
}

/**
 * Full-bleed image component with magazine glossy effect
 * and optional save indicator (red heart badge)
 */
export function PlaceCardImage({
  imageUrl,
  imageAlt,
  aspectRatio,
  showSaveIndicator = false,
  className,
}: PlaceCardImageProps) {
  // Calculate aspect ratio padding
  const aspectRatioPadding = {
    '1:1': 'pb-[100%]',    // 1/1 = 100%
    '6:5': 'pb-[83.33%]',  // 5/6 = 83.33%
    '16:9': 'pb-[56.25%]', // 9/16 = 56.25%
    '4:3': 'pb-[75%]',     // 3/4 = 75%
  }[aspectRatio]

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Aspect ratio container */}
      <div className={cn('relative w-full', aspectRatioPadding)}>
        {/* Image */}
        <img
          src={imageUrl}
          alt={imageAlt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />

        {/* Magazine glossy effect - subtle specular highlight */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(
              135deg,
              rgba(255, 255, 255, 0.15) 0%,
              rgba(255, 255, 255, 0) 40%,
              rgba(255, 255, 255, 0) 60%,
              rgba(255, 255, 255, 0.05) 100%
            )`,
          }}
        />

        {/* Save indicator - red heart badge (used sparingly) */}
        {showSaveIndicator && (
          <div className="absolute top-1.5 right-1.5 z-10">
            <div className="flex items-center justify-center w-4 h-4 bg-white rounded-full shadow-md">
              <span
                className="text-red-500 text-sm leading-none"
                style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' }}
                aria-label="Saved"
              >
                ❤️
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
